import type { Squid as BaseSquid } from "@0xsquid/sdk";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { defaultConfigValues } from "../../core/constants";
import { AppConfig } from "../../core/types/config";
import { SwapParams } from "../../core/types/swap";
import { Chain, Token } from "../../core/types/tokens";
import { TransactionParams } from "../../core/types/transaction";

export interface Squid extends BaseSquid {
  tokens: Token[];
  chains: Chain[];
}

// ====================
// SQUID
// ====================
interface SquidState {
  squid?: Squid;
  maintenanceMode: {
    active: boolean;
    message?: string;
  };
}

export const useSquidStore = create<SquidState>(() => ({
  squid: undefined,
  maintenanceMode: {
    active: false,
    message: undefined,
  },
}));

// ====================
// CONFIG
// ====================
export interface ConfigState {
  config: AppConfig;
  isInitialized?: boolean;
}

export const useConfigStore = create<ConfigState>(() => ({
  config: defaultConfigValues,
  isInitialized: false,
}));

// ====================
// TRANSACTION
// ====================
interface TransactionState {
  fromPrice?: string;
  txLocalId?: string;
  transactions: Record<string, TransactionParams>;
  currentTransaction?: TransactionParams;
  setTransactionState: (
    id: string | undefined,
    transaction: TransactionParams
  ) => void;
  getTransaction: (id: string) => TransactionParams | undefined;
}

export const useTransactionStore = create<TransactionState>((set, get) => ({
  fromPrice: undefined,
  txLocalId: undefined,
  transactions: {},
  currentTransaction: undefined,
  setTransactionState(txId, tx) {
    if (!txId) return;

    set((state) => {
      const newTransactions = { ...state.transactions, [txId]: tx };

      return {
        transactions: newTransactions,
        currentTransaction:
          txId === state.txLocalId ? tx : state.currentTransaction,
      };
    });
  },
  getTransaction(id) {
    return get().transactions[id];
  },
}));

interface SwapRoutePersistState {
  swapRoute?: SwapParams;
  resetAddresses: () => void;
}

export const useSwapRoutePersistStore = create<SwapRoutePersistState>()(
  persist(
    (set) => ({
      swapRoute: undefined,
      resetAddresses: () =>
        set((state) => ({
          swapRoute: state.swapRoute
            ? {
                ...state.swapRoute,
                destinationAddress: undefined,
                fallbackAddress: undefined,
                depositRefundAddress: undefined,
              }
            : undefined,
        })),
    }),
    {
      name: "squid-swap-route.store",
      onRehydrateStorage: () => (state) => {
        // Reset addresses on page load/rehydration
        state?.resetAddresses();
      },
    }
  )
);
